import * as THREE from 'three';
import Application from '../Application';
import UIEventBus from '../UI/EventBus';
/**
 * Personal desk props: sticky notes + a clickable coffee mug + resume paper.
 *
 * Everything uses MeshBasicMaterial so it matches the baked (unlit) scene.
 * World units: the baked models are scaled x900, desk top sits at y ≈ -452.
 */
const DESK_Y = -451.5;
const COFFEE_MESSAGES = [
    '☕ Refilled. Bugs fear this mug.',
    '☕ Cup #2: regression suite is running.',
    '☕ Cup #3: flaky tests are getting nervous.',
    '☕ Cup #4: okay... maybe switch to water.',
];
export default class DeskProps {
    constructor(hitboxes, coffeeSteam) {
        this.application = new Application();
        this.scene = this.application.scene;
        this.resources = this.application.resources;
        this.camera = this.application.camera;
        this.sizes = this.application.sizes;
        this.hitboxes = hitboxes;
        this.coffeeSteam = coffeeSteam;
        this.animated = [];
        this.coffeeCups = 0;
        this.createResumePaper();
        this.createStickyNotes();
        this.createCoffeeHitbox();
        this.createAiStickers();
    }
    /* ------------------------------------------------------------------ */
    /* Resume paper (the loose sheet on the left of the desk)              */
    /* ------------------------------------------------------------------ */
    createResumePaper() {
        // Corners of the baked "paper" quad (decor.glb x900):
        // (-2572, 814) (-2066, 449) (-1555, 1159) (-2061, 1524) on y = -445
        const WIDTH = 624;
        const HEIGHT = 875;
        const center = new THREE.Vector3(-2063.5, -444, 986.5);
        // Sheet is rotated ~36deg around Y; its "top" edge points to -x/-z
        const yaw = Math.atan2(365, 506);
        const top = new THREE.Vector3(-Math.sin(yaw), 0, -Math.cos(yaw));
        const texture = this.resources.items.texture.deskResumeTexture;
        if (texture) {
            this.stabilizeCanvasTexture(texture);
        }
        const material = new THREE.MeshBasicMaterial(texture
            ? { map: texture, color: 0xe4e2dc }
            : { color: 0xe4e2dc });
        const sheet = new THREE.Mesh(new THREE.PlaneGeometry(WIDTH, HEIGHT), material);
        sheet.name = 'resumePaper';
        sheet.position.copy(center);
        sheet.rotation.set(-Math.PI / 2, yaw, 0, 'YXZ');
        this.scene.add(sheet);
        const targets = [sheet];
        const bakedPaper = this.resources.items.gltfModel.decorModel.scene
            .getObjectByName('paper');
        if (bakedPaper)
            targets.push(bakedPaper);
        const anim = this.addAnimated(sheet, new THREE.Vector3(0, 14, 0), 1.03);
        this.hitboxes.register('resume', targets, {
            label: 'Resume — click to read',
            onHoverChange: (h) => (anim.hover = h),
            onClick: () => {
                // Look down at the sheet from behind its bottom edge so the
                // page reads upright on screen
                const offset = top
                    .clone()
                    .multiplyScalar(-720)
                    .add(new THREE.Vector3(0, 1800, 0));
                this.focus(center.clone(), offset);
                this.open('resume');
            },
        });
    }
    /* ------------------------------------------------------------------ */
    /* Sticky notes                                                        */
    /* ------------------------------------------------------------------ */
    createStickyNotes() {
        const notes = [
            {
                // Right edge of the monitor bezel (bezel face tilts back ~3°)
                kind: 'note-tip',
                label: 'QA tip — click to read',
                color: '#f6e27f',
                lines: ['QA TIP #1', 'No repro steps?', "It's just a", 'rumor ;)'],
                position: new THREE.Vector3(790, 1225, 466),
                rotation: new THREE.Euler(-0.05, 0, -0.1, 'XYZ'),
                size: 210,
                hoverOffset: new THREE.Vector3(0, 0, 20),
                focusOffset: new THREE.Vector3(-150, 40, 900),
            },
            {
                // Left edge of the monitor bezel, lower — developer mottos
                kind: 'note-motto',
                label: 'Motto — click to read',
                color: '#f6a6c1',
                lines: ['MOTTO', 'Ship it.', 'Then make', 'it right.'],
                position: new THREE.Vector3(-790, 690, 488),
                rotation: new THREE.Euler(-0.05, 0, 0.14, 'XYZ'),
                size: 210,
                hoverOffset: new THREE.Vector3(0, 0, 20),
                focusOffset: new THREE.Vector3(150, 40, 900),
            },
            {
                // Lying flat on the desk, right of the resume sheet
                kind: 'note-contact',
                label: 'Contact — click',
                color: '#9fe3c4',
                lines: ['SAY HI! ✉', 'sencersenyurt', '@gmail.com', 'in/irfansenyurt'],
                position: new THREE.Vector3(-1310, DESK_Y + 1.5, 1270),
                rotation: new THREE.Euler(-Math.PI / 2, 0.28, 0, 'YXZ'),
                size: 230,
                hoverOffset: new THREE.Vector3(0, 18, 0),
                focusOffset: new THREE.Vector3(0, 700, 520),
            },
        ];
        notes.forEach((config) => this.createStickyNote(config));
    }
    createStickyNote(config) {
        const size = config.size;
        const geometry = new THREE.PlaneGeometry(size, size, 1, 8);
        // Slight curl on the bottom edge so it reads as paper, not a decal
        const pos = geometry.attributes.position;
        for (let i = 0; i < pos.count; i++) {
            const y = pos.getY(i);
            const v = (size / 2 - y) / size; // 0 at top, 1 at bottom
            const curl = v > 0.55 ? Math.pow((v - 0.55) / 0.45, 2) * 14 : 0;
            pos.setZ(i, curl);
        }
        pos.needsUpdate = true;
        const material = new THREE.MeshBasicMaterial({
            map: this.createStickyTexture(config.color, config.lines),
            side: THREE.DoubleSide,
        });
        const mesh = new THREE.Mesh(geometry, material);
        mesh.name = 'stickyNote_' + config.kind;
        mesh.position.copy(config.position);
        mesh.rotation.copy(config.rotation);
        this.scene.add(mesh);
        const anim = this.addAnimated(mesh, config.hoverOffset, 1.06);
        this.hitboxes.register(config.kind, mesh, {
            label: config.label,
            onHoverChange: (h) => (anim.hover = h),
            onClick: () => {
                this.focus(config.position.clone(), config.focusOffset.clone());
                this.open(config.kind);
            },
        });
    }
    /* ------------------------------------------------------------------ */
    /* AI laptop-style stickers on the monitor bezel                       */
    /* ------------------------------------------------------------------ */
    createAiStickers() {
        const stickers = [
            {
                // Lower-right corner of the side bezel
                id: 'sticker-claude',
                label: 'Claude sticker',
                x: 712,
                y: 560,
                size: 104,
                roll: -0.14,
                draw: 'claude',
                toast: '✳ Claude (Anthropic): second reviewer on every test plan.',
            },
            {
                // Monitor chin, right of center
                id: 'sticker-grok',
                label: 'Grok Bot sticker',
                x: 585,
                y: 362,
                size: 112,
                roll: 0.1,
                draw: 'grok',
                toast: '🤖 Grok Bot: handles the repetitive stuff so I don\'t.',
            },
        ];
        // Snap each sticker onto the real bezel surface by raycasting
        // the baked computer model from the front.
        this.scene.updateMatrixWorld(true);
        const raycaster = new THREE.Raycaster();
        const skip = new Set(this.animated.map((a) => a.object));
        stickers.forEach((cfg) => {
            raycaster.set(new THREE.Vector3(cfg.x, cfg.y, 3000), new THREE.Vector3(0, 0, -1));
            const hits = raycaster.intersectObjects(this.scene.children, true);
            let point = new THREE.Vector3(cfg.x, cfg.y, 505);
            let normal = new THREE.Vector3(0, 0.05, 1).normalize();
            for (const hit of hits) {
                let o = hit.object;
                let skipped = false;
                while (o) {
                    if (skip.has(o))
                        skipped = true;
                    o = o.parent;
                }
                const mat = hit.object.material;
                if (skipped ||
                    hit.object.userData.isHitbox ||
                    !hit.object.isMesh ||
                    (mat && mat.transparent && mat.blending !== THREE.NoBlending)) {
                    continue;
                }
                point = hit.point.clone();
                if (hit.face) {
                    normal = hit.face.normal
                        .clone()
                        .transformDirection(hit.object.matrixWorld)
                        .normalize();
                    if (normal.z < 0)
                        normal.negate();
                }
                break;
            }
            const material = new THREE.MeshBasicMaterial({
                map: this.createAiStickerTexture(cfg.draw),
                alphaTest: 0.5,
                polygonOffset: true,
                polygonOffsetFactor: -2,
                polygonOffsetUnits: -4,
            });
            const mesh = new THREE.Mesh(new THREE.PlaneGeometry(cfg.size, cfg.size), material);
            mesh.name = cfg.id;
            mesh.position.copy(point).addScaledVector(normal, 1.2);
            mesh.lookAt(point.clone().add(normal));
            mesh.rotateZ(cfg.roll);
            this.scene.add(mesh);
            const anim = this.addAnimated(mesh, normal.clone().multiplyScalar(6), 1.12);
            this.hitboxes.register(cfg.id, mesh, {
                label: cfg.label + ' — click',
                onHoverChange: (h) => (anim.hover = h),
                onClick: () => UIEventBus.dispatch('deskToast', { text: cfg.toast }),
            });
        });
    }
    createAiStickerTexture(kind) {
        const S = 512;
        const canvas = document.createElement('canvas');
        canvas.width = S;
        canvas.height = S;
        const ctx = canvas.getContext('2d');
        const roundRect = (x, y, w, h, r) => {
            ctx.beginPath();
            ctx.moveTo(x + r, y);
            ctx.arcTo(x + w, y, x + w, y + h, r);
            ctx.arcTo(x + w, y + h, x, y + h, r);
            ctx.arcTo(x, y + h, x, y, r);
            ctx.arcTo(x, y, x + w, y, r);
            ctx.closePath();
        };
        if (kind === 'grok') {
            // Die-cut white border + matte black vinyl
            ctx.fillStyle = '#ece8e1';
            roundRect(16, 56, S - 32, S - 112, 92);
            ctx.fill();
            ctx.fillStyle = '#16171a';
            roundRect(38, 78, S - 76, S - 156, 74);
            ctx.fill();
            // Little bot head
            ctx.strokeStyle = '#ece8e1';
            ctx.fillStyle = '#ece8e1';
            ctx.lineWidth = 14;
            roundRect(176, 148, 160, 118, 30);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(256, 148);
            ctx.lineTo(256, 116);
            ctx.stroke();
            ctx.beginPath();
            ctx.arc(256, 108, 13, 0, Math.PI * 2);
            ctx.fill();
            ctx.beginPath();
            ctx.arc(222, 206, 15, 0, Math.PI * 2);
            ctx.arc(290, 206, 15, 0, Math.PI * 2);
            ctx.fill();
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.font = "bold 74px 'Consolas', 'Menlo', monospace";
            ctx.fillText('GROK BOT', 256, 340, 400);
        }
        else {
            // Die-cut white border + terracotta disc
            ctx.fillStyle = '#ece8e1';
            ctx.beginPath();
            ctx.arc(256, 256, 240, 0, Math.PI * 2);
            ctx.fill();
            ctx.fillStyle = '#c96f4f';
            ctx.beginPath();
            ctx.arc(256, 256, 216, 0, Math.PI * 2);
            ctx.fill();
            // Spark / asterisk mark
            ctx.fillStyle = '#f5ece1';
            const cx = 256;
            const cy = 200;
            const rays = 12;
            for (let i = 0; i < rays; i++) {
                const a = (i / rays) * Math.PI * 2;
                const len = i % 2 === 0 ? 92 : 70;
                ctx.save();
                ctx.translate(cx, cy);
                ctx.rotate(a);
                ctx.beginPath();
                ctx.moveTo(-9, 0);
                ctx.lineTo(0, -len);
                ctx.lineTo(9, 0);
                ctx.closePath();
                ctx.fill();
                ctx.restore();
            }
            ctx.beginPath();
            ctx.arc(cx, cy, 18, 0, Math.PI * 2);
            ctx.fill();
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.font = "bold 76px Georgia, 'Times New Roman', serif";
            ctx.fillText('Claude', 256, 362, 340);
        }
        // Faint vinyl sheen, kept subtle so it doesn't flash on orbit
        const sheen = ctx.createLinearGradient(0, 0, S, S);
        sheen.addColorStop(0, 'rgba(255,255,255,0)');
        sheen.addColorStop(0.45, 'rgba(255,255,255,0.07)');
        sheen.addColorStop(0.55, 'rgba(255,255,255,0)');
        ctx.globalCompositeOperation = 'source-atop';
        ctx.fillStyle = sheen;
        ctx.fillRect(0, 0, S, S);
        ctx.globalCompositeOperation = 'source-over';
        const texture = new THREE.CanvasTexture(canvas);
        texture.encoding = THREE.sRGBEncoding;
        this.stabilizeCanvasTexture(texture);
        return texture;
    }
    /* ------------------------------------------------------------------ */
    /* Coffee mug                                                          */
    /* ------------------------------------------------------------------ */
    createCoffeeHitbox() {
        // Bounds of the baked "coffee" mesh (decor.glb x900)
        const box = this.hitboxes.createBox('coffeeHitbox', new THREE.Vector3(1740, -200, 910), new THREE.Vector3(480, 520, 340));
        const targets = [box];
        if (this.coffeeSteam && this.coffeeSteam.model) {
            targets.push(this.coffeeSteam.model.mesh);
        }
        this.hitboxes.register('coffee', targets, {
            label: 'Coffee — click to refill',
            onClick: () => {
                if (this.coffeeSteam)
                    this.coffeeSteam.boost();
                const text = COFFEE_MESSAGES[Math.min(this.coffeeCups, COFFEE_MESSAGES.length - 1)];
                this.coffeeCups++;
                UIEventBus.dispatch('deskToast', { text });
            },
        });
    }
    /* ------------------------------------------------------------------ */
    /* Helpers                                                             */
    /* ------------------------------------------------------------------ */
    open(kind) {
        UIEventBus.dispatch('deskItemOpen', { kind });
    }
    /**
     * Zoom onto `center` from `center + offset`. On wide screens the info
     * panel sits on the right, so shift the focal point right to keep the
     * object visible on the left half.
     */
    focus(center, offset) {
        const position = center.clone().add(offset);
        const focal = center.clone();
        if (this.sizes.width > 900) {
            const dir = center.clone().sub(position).normalize();
            const right = new THREE.Vector3()
                .crossVectors(dir, new THREE.Vector3(0, 1, 0))
                .normalize();
            focal.add(right.multiplyScalar(offset.length() * 0.22));
        }
        this.camera.focusOn(position, focal);
    }
    addAnimated(object, hoverOffset, hoverScale) {
        const anim = {
            object,
            basePosition: object.position.clone(),
            baseScale: object.scale.x,
            hoverOffset,
            hoverScale,
            hover: false,
            t: 0,
        };
        this.animated.push(anim);
        return anim;
    }
    update() {
        for (const a of this.animated) {
            a.t += ((a.hover ? 1 : 0) - a.t) * 0.18;
            if (a.t < 0.0001)
                a.t = 0;
            a.object.position
                .copy(a.basePosition)
                .addScaledVector(a.hoverOffset, a.t);
            const s = a.baseScale * (1 + (a.hoverScale - 1) * a.t);
            a.object.scale.set(s, s, s);
        }
    }
    /** Stable canvas/map sampling — no mip shimmer when the camera breathes. */
    stabilizeCanvasTexture(texture) {
        texture.generateMipmaps = false;
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;
        texture.anisotropy = 1;
        texture.needsUpdate = true;
        return texture;
    }
    createStickyTexture(color, lines) {
        const canvas = document.createElement('canvas');
        canvas.width = 512;
        canvas.height = 512;
        const ctx = canvas.getContext('2d');
        // Paper with a soft vertical gradient
        const grad = ctx.createLinearGradient(0, 0, 0, 512);
        grad.addColorStop(0, shade(color, -0.1));
        grad.addColorStop(0.18, color);
        grad.addColorStop(1, shade(color, -0.06));
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, 512, 512);
        // Adhesive strip
        ctx.fillStyle = 'rgba(0,0,0,0.05)';
        ctx.fillRect(0, 0, 512, 70);
        // Handwriting
        ctx.fillStyle = '#25303f';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const font = "'Segoe Print', 'Ink Free', 'Bradley Hand', 'Comic Sans MS', 'Marker Felt', cursive";
        lines.forEach((line, i) => {
            const isTitle = i === 0;
            ctx.font = `${isTitle ? 'bold 62px' : '50px'} ${font}`;
            const y = 135 + i * 92;
            ctx.save();
            ctx.translate(256, y);
            ctx.rotate((i % 2 === 0 ? -1 : 1) * 0.015);
            ctx.fillText(line, 0, 0, 470);
            ctx.restore();
            if (isTitle) {
                ctx.strokeStyle = '#25303f';
                ctx.lineWidth = 4;
                ctx.beginPath();
                ctx.moveTo(110, y + 38);
                ctx.quadraticCurveTo(256, y + 46, 402, y + 34);
                ctx.stroke();
            }
        });
        const texture = new THREE.CanvasTexture(canvas);
        texture.encoding = THREE.sRGBEncoding;
        this.stabilizeCanvasTexture(texture);
        return texture;
    }
    createGlassTexture() {
        const canvas = document.createElement('canvas');
        canvas.width = 256;
        canvas.height = 256;
        const ctx = canvas.getContext('2d');
        // Soft warm sheen — no hard white streak that flashes on orbit
        const grad = ctx.createLinearGradient(0, 0, 256, 256);
        grad.addColorStop(0, 'rgba(255,255,255,0)');
        grad.addColorStop(0.4, 'rgba(255,255,255,0)');
        grad.addColorStop(0.48, 'rgba(240,232,220,0.22)');
        grad.addColorStop(0.55, 'rgba(255,255,255,0)');
        grad.addColorStop(0.62, 'rgba(240,232,220,0.1)');
        grad.addColorStop(0.68, 'rgba(255,255,255,0)');
        grad.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, 256, 256);
        return new THREE.CanvasTexture(canvas);
    }
}
/** Lighten (amount > 0) or darken (amount < 0) a #rrggbb color */
function shade(hex, amount) {
    const n = parseInt(hex.slice(1), 16);
    const mix = (c) => Math.round(Math.min(255, Math.max(0, amount < 0 ? c * (1 + amount) : c + (255 - c) * amount)));
    const r = mix((n >> 16) & 255);
    const g = mix((n >> 8) & 255);
    const b = mix(n & 255);
    return `rgb(${r}, ${g}, ${b})`;
}
